import { Button } from "@/components/Button/Button";
import { POSTHOG_VISIBLE } from "@/constants/posthog";
import { OnboardingCard } from "@/pages/Onboarding/Components/OnboardingCard";
import { OnboardingHeader } from "@/pages/Onboarding/Components/OnboardingHeader";
import { useCreateOrganisationApiV1OrganisationsPostMutation } from "@/store/clientApi";
import { setRegion } from "@/store/slices/orgSlice";
import { store } from "@/store/store";
import { OnboardingUserData } from "@/typed/OnboardingUserData";
import { COUNTRIES, CountryOption } from "@/utils/countries";
import { ensureError, localizeError } from "@/utils/helpers";
import { type Region, REGIONS } from "@/utils/regionApi";
import {
  Alert,
  Autocomplete,
  Box,
  Divider,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  TextField,
  Theme,
  Typography
} from "@mui/material";
import { jwtDecode, JwtPayload } from "jwt-decode";
import React, { FC, useState } from "react";
import toast from "react-hot-toast";
import { Trans, useTranslation } from "react-i18next";

/**
 * Displays a warning about data residency and compliance implications of region selection,
 * with the option to expand for more information.
 */
const DataWarning = () => {
  const { t } = useTranslation("onboarding");
  const [collapsed, setCollapsed] = useState(true);

  return (
    <Alert
      severity="warning"
      sx={{
        backgroundColor: (theme: Theme) => theme.palette.warning.light,
        color: (theme: Theme) => theme.palette.text.primary,
        borderRadius: "8px",
        marginBottom: "4px",
        py: "8px"
      }}
    >
      <Typography sx={{ fontSize: "14px" }}>
        <Trans
          components={{
            bold: <strong style={{ fontWeight: 600, display: "block", marginTop: "8px", marginBottom: collapsed ? "0px" : "8px" }} />
          }}
          i18nKey={"label.regionDescription"}
          t={t}
          context={collapsed ? "short" : "long"}
        />
      </Typography>
      <Link
        component="button"
        type="button"
        variant="body2"
        sx={{ marginTop: "8px", display: "block" }}
        aria-expanded={!collapsed}
        onClick={() => setCollapsed(!collapsed)}
      >
        {collapsed ? t("common:button.showMore") : t("common:button.showLess")}
      </Link>
    </Alert>
  );
};

export const OnboardingForm: FC = () => {
  const { t } = useTranslation("onboarding");
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [fullName, setFullName] = useState("");
  const [fullNameError, setFullNameError] = useState("");
  const [region, setRegionValue] = useState<Region>("AU"); // TODO: https://asiga.atlassian.net/browse/MESH-1021 - Default to "US"
  const [country, setCountry] = useState<CountryOption | null>(null);
  const [countryError, setCountryError] = useState("");
  const [organisation, setOrganisation] = useState("");
  const [orgError, setOrgError] = useState("");
  const [createOrg] = useCreateOrganisationApiV1OrganisationsPostMutation();

  const token = new URL(window.location.href).searchParams.get("session_token");
  const state = new URL(window.location.href).searchParams.get("state");
  const ref = new URL(window.location.href).searchParams.get("ref");

  const submitForm = async () => {
    setLoadingSubmit(true);
    if (fullName === "") {
      setFullNameError("Field is required");
    } else {
      setFullNameError("");
    }

    if (country === null) {
      setCountryError("Field is required");
    } else {
      setCountryError("");
    }

    if (organisation === "") {
      setOrgError("Field is required");
    } else {
      setOrgError("");
    }

    // TODO: https://asiga.atlassian.net/browse/MESH-1021 - Re-enable AI policy acceptance when the policy is ready and link is available
    // if (!acceptedAiPolicy) {
    //   setAiPolicyError("Field is required");
    // } else {
    //   setAiPolicyError("");
    // }

    if (fullName !== "" && country !== null && organisation !== "" /* && acceptedAiPolicy */) {
      const newData: OnboardingUserData = {
        fullName: fullName,
        region: region,
        country: country.code,
        organisation: organisation
      };

      try {
        const decoded = jwtDecode<JwtPayload & { continue_uri: string }>(token ?? "");
        // region must be set in Redux before creating the organisation
        // so that the API request is sent to the correct regional endpoint
        store.dispatch(setRegion(newData.region));

        await createOrg({
          organisationCreateRequestDto: {
            name: newData.organisation,
            region: newData.region,
            country: newData.country,
            affiliate_code: ref ?? undefined,
            user_name: newData.fullName,
            consents: ["ai-training"]
          }
        }).unwrap();

        window.location.href = `${decoded.continue_uri}?state=${state}`;
      } catch (err) {
        const error = ensureError(err);
        toast.error(localizeError(t, error));
        setLoadingSubmit(false);
      }
    }
    setLoadingSubmit(false);
  };

  const handleOnChangeFullName = (nameValue: string) => {
    if (nameValue === "") {
      setFullNameError("Field is required");
    } else {
      setFullNameError("");
    }

    setFullName(nameValue);
  };

  const handleOnOrgChange = (orgValue: string) => {
    if (orgValue === "") {
      setOrgError("Field is required");
    } else {
      setOrgError("");
    }

    setOrganisation(orgValue);
  };

  return (
    <OnboardingCard>
      <OnboardingHeader />
      <Box
        component="form"
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          gap: "0.8em"
        }}
        noValidate
        autoComplete="off"
      >
        <FormControl fullWidth>
          <TextField
            id="full-name"
            label={t("label.fullName")}
            variant="outlined"
            error={!!fullNameError}
            value={fullName}
            onChange={(e) => handleOnChangeFullName(e.target.value)}
            required
          />
        </FormControl>
        <Autocomplete
          id="country-select"
          options={COUNTRIES}
          getOptionLabel={(option) => option.name}
          getOptionKey={(option) => option.code}
          isOptionEqualToValue={(option, value) => option.code === value.code}
          value={country}
          onChange={(_e, newValue) => {
            setCountry(newValue);
            setCountryError(newValue === null ? "Field is required" : "");
          }}
          renderInput={(params) => <TextField {...params} label={t("label.country")} error={!!countryError} required />}
        />
        <TextField
          id="organisation-name"
          label={t("label.organisationName")}
          variant="outlined"
          error={!!orgError}
          value={organisation}
          onChange={(e) => handleOnOrgChange(e.target.value)}
          required
        />
        <Divider sx={{ mx: "-40px", my: "1em" }} />
        <DataWarning />
        <FormControl fullWidth>
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <InputLabel id="region-label" required {...POSTHOG_VISIBLE}>
              {t("label.region")}
            </InputLabel>
          </Box>

          <Select
            labelId="region-label"
            id="region-select"
            label={t("label.region")}
            value={region}
            onChange={(e) => setRegionValue(e.target.value as Region)}
            required
          >
            {REGIONS.map((item) => (
              <MenuItem key={item.code} value={item.code}>
                {item.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {region === "US" && (
          <Typography variant="body2" sx={{ color: "text.secondary" }} {...POSTHOG_VISIBLE}>
            {t("message.hipaaBaa")}
          </Typography>
        )}
        {/* 
        TODO: https://asiga.atlassian.net/browse/MESH-1021 - Re-enable AI policy acceptance when the policy is ready and link is available
         <FormControlLabel
          control={
            <Checkbox
              checked={acceptedAiPolicy}
              onChange={(_, checked) => {
                setAcceptedAiPolicy(checked);
                setAiPolicyError(checked ? "" : "Field is required");
              }}
              sx={aiPolicyError ? { color: "error.main" } : undefined}
            />
          }
          label={
            <Typography variant="body2" sx={aiPolicyError ? { color: "error.main" } : undefined} {...POSTHOG_VISIBLE}>
              <Trans
                components={{
                  a: (
                    <Link
                      component="button"
                      type="button"
                      onClick={navigateToAiUsagePolicy}
                      sx={aiPolicyError ? { color: "error.main" } : undefined}
                      {...POSTHOG_VISIBLE}
                    />
                  )
                }}
                t={t}
                i18nKey="message.acceptAiUsagePolicy"
              />
            </Typography>
          }
        /> */}
        <Button
          disabled={loadingSubmit}
          sx={{
            marginTop: "24px",
            height: "52px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center"
          }}
          type="submit"
          onClick={(e: React.SyntheticEvent) => {
            e.preventDefault();
            submitForm();
          }}
        >
          {t("common:button.continue")}
        </Button>
      </Box>
    </OnboardingCard>
  );
};
